import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CartService } from 'app/core/cart/cart.service';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

export interface TicketingCategory {
    id: string;
    name: string;
    tagline: string;
    badge: string;
    badgeColor: string;
    price: number;
    priceFormatted: string;
    originalPrice?: number;
    originalPriceFormatted?: string;
    image: string;
    description: string;
    longDescription: string;
    bestFor: string;
    capacity: string;
    features: string[];
    deliverables: string[];
    hardwareIncluded: string[];
    isPopular?: boolean;
    showMore?: boolean;
}

interface ApiNonThemedPackage {
    id: string;
    non_themed_product_id: string;
    non_themed_product_name: string;
    name: string;
    description: string | null;
    regular_price: number;
    price: number;
    is_active: boolean;
    product_package_type: string;
    devices: any[];
}

@Component({
    selector: 'app-smart-ticketing',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './smart-ticketing.component.html',
    styleUrls: ['./smart-ticketing.component.scss'],
})
export class SmartTicketingComponent implements OnInit {
    whatsappNumber = '6282260084847';
    isLoading: boolean = false;

    // Array kosong - akan diisi dari API
    categories: TicketingCategory[] = [];

    selectedCategory: TicketingCategory | null = null;
    isConsultationModalOpen: boolean = false;
    consultationForm = {
        eventName: '',
        eventDate: '',
        eventLocation: '',
        expectedAttendees: '500 - 1000',
        packageInterest: 'Smart Ticketing Basic',
        contactName: '',
        contactPhone: '',
        notes: ''
    };

    constructor(
        private cartService: CartService,
        private router: Router,
        private apiService: ApiService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        this.fetchSmartTicketingPackages();
    }

    async fetchSmartTicketingPackages(): Promise<void> {
        this.isLoading = true;

        try {
            const apiEndpoint = '/api/v1/front/public/non-themed-product-packages/list?paginate=false';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Fallback fetch error:', e);
                }
            }

            if (response && response.data && Array.isArray(response.data.list) && response.data.list.length > 0) {
                // Filter hanya Smart Ticketing packages
                const smartTicketingPackages = response.data.list.filter((pkg: ApiNonThemedPackage) =>
                    pkg.non_themed_product_name === 'Smart Ticketing & QR Gate System'
                );

                if (smartTicketingPackages.length > 0) {
                    this.categories = this.transformSmartTicketingPackages(smartTicketingPackages);

                    // Set selectedCategory ke yang pertama jika ada
                    if (this.categories.length > 0) {
                        this.selectedCategory = this.categories[0];
                        this.consultationForm.packageInterest = this.categories[0].name;
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching smart ticketing packages:', error);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    private transformSmartTicketingPackages(apiList: ApiNonThemedPackage[]): TicketingCategory[] {
        return apiList.map((item, index) => {
            const isBasic = item.name.toLowerCase().includes('basic');

            // Hanya ambil image dan badge dari config, bukan deskripsi
            const imageAndBadge = this.getImageAndBadge(item.name);

            return {
                id: item.id,
                name: item.name,
                tagline: item.description || '', // Gunakan description dari API atau kosong
                badge: imageAndBadge.badge,
                badgeColor: 'bg-emerald-600 text-white',
                price: item.price,
                priceFormatted: this.formatCurrency(item.price),
                originalPrice: item.regular_price,
                originalPriceFormatted: this.formatCurrency(item.regular_price),
                image: imageAndBadge.image,
                description: item.description || '', // Gunakan dari API atau kosong
                longDescription: '', // Kosongkan karena tidak ada di API
                bestFor: '', // Kosongkan karena tidak ada di API
                capacity: '', // Kosongkan karena tidak ada di API
                features: this.getBenefits(item), // Parse benefits dari API
                deliverables: [], // Kosongkan karena tidak ada di API
                hardwareIncluded: [], // Kosongkan karena tidak ada di API
                isPopular: isBasic,
                showMore: false
            };
        });
    }

    private getImageAndBadge(packageName: string): { image: string; badge: string } {
        const isBasic = packageName.toLowerCase().includes('basic');

        if (isBasic) {
            return {
                image: '/assets/visidea/ezgate-basic.png',
                badge: 'POPULAR CHOICE'
            };
        } else {
            return {
                image: '/assets/visidea/ezgate-premium.png',
                badge: 'ENTERPRISE & FESTIVAL'
            };
        }
    }

    private getBenefits(pkg: any): string[] {
        // Try multiple possible benefit sources
        const benefitData =
            pkg?.properties?.benefit ||
            pkg?.benefit ||
            pkg?.benefits ||
            pkg?.features ||
            pkg?.description;

        if (!benefitData) {
            return [];
        }

        // If it's already an array, return it
        if (Array.isArray(benefitData)) {
            return benefitData.filter(b => b && typeof b === 'string');
        }

        // If it's a string, try parsing as JSON first
        if (typeof benefitData === 'string') {
            try {
                const parsed = JSON.parse(benefitData);
                if (Array.isArray(parsed)) {
                    return parsed.filter(b => b && typeof b === 'string');
                }
            } catch (e) {
                // Not JSON, try splitting by newlines
                const lines = benefitData
                    .split(/\r?\n/)
                    .map(line => line.trim())
                    .filter(line => line.length > 0);

                if (lines.length > 0) {
                    return lines;
                }
            }
        }

        return [];
    }


    private formatCurrency(val: number): string {
        if (!val && val !== 0) return 'Rp0';
        return 'Rp' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    toggleShowMore(category: TicketingCategory, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const currentStatus = category.showMore;
        this.categories.forEach(c => c.showMore = false);
        category.showMore = !currentStatus;
    }

    selectCategory(category: TicketingCategory): void {
        this.selectedCategory = category;
        this.consultationForm.packageInterest = category.name;
    }

    addToCart(category: TicketingCategory, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const cartItemPayload = {
            id: `ticketing_${category.id}`,
            itemable_id: category.id,
            name: `${category.name}`,
            category: 'Smart Ticketing & Gate System',
            price: category.price,
            image: category.image,
            description: category.tagline || category.description || 'Sistem Tiket & Gate QR',
            selectedOption: 'Paket Layanan',
            custom_options: {
                package_id: category.id,
                package_name: category.name,
                category: 'Smart Ticketing & Gate System'
            }
        };
        this.cartService.addItem(cartItemPayload, 1);
    }

    checkout(category: TicketingCategory, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const cartItemPayload = {
            id: `ticketing_${category.id}`,
            itemable_id: category.id,
            name: `${category.name}`,
            category: 'Smart Ticketing & Gate System',
            price: category.price,
            image: category.image,
            description: category.tagline || category.description || 'Sistem Tiket & Gate QR',
            selectedOption: 'Paket Layanan',
            custom_options: {
                package_id: category.id,
                package_name: category.name,
                category: 'Smart Ticketing & Gate System'
            }
        };
        void this.cartService.startDirectCheckout(cartItemPayload, 1);
    }

    openConsultationModal(category?: TicketingCategory): void {
        if (category) {
            this.selectedCategory = category;
            this.consultationForm.packageInterest = category.name;
        }
        this.isConsultationModalOpen = true;
    }

    closeConsultationModal(): void {
        this.isConsultationModalOpen = false;
    }

    submitConsultation(): void {
        if (!this.consultationForm.eventName || !this.consultationForm.contactPhone) {
            Swal.fire({
                icon: 'warning',
                title: 'Data Belum Lengkap',
                text: 'Harap isi nama event dan nomor WhatsApp Anda.',
                confirmButtonColor: '#202367'
            });
            return;
        }

        const msg = `Halo Visidea Studio, saya ingin konsultasi layanan *${this.consultationForm.packageInterest}*:%0A%0A` +
            `• *Nama Acara*: ${encodeURIComponent(this.consultationForm.eventName)}%0A` +
            `• *Tanggal Acara*: ${encodeURIComponent(this.consultationForm.eventDate || '-')}%0A` +
            `• *Lokasi / Venue*: ${encodeURIComponent(this.consultationForm.eventLocation || '-')}%0A` +
            `• *Estimasi Pengunjung*: ${encodeURIComponent(this.consultationForm.expectedAttendees)}%0A` +
            `• *Pilihan Kategori*: ${encodeURIComponent(this.consultationForm.packageInterest)}%0A` +
            `• *Nama Kontak*: ${encodeURIComponent(this.consultationForm.contactName || '-')}%0A` +
            `• *No. WhatsApp*: ${encodeURIComponent(this.consultationForm.contactPhone)}%0A` +
            `• *Catatan Tambahan*: ${encodeURIComponent(this.consultationForm.notes || '-')}%0A%0A` +
            `Mohon dibantu proposal teknis dan penawaran harganya. Terima kasih!`;

        const waUrl = `https://wa.me/${this.whatsappNumber}?text=${msg}`;
        this.closeConsultationModal();

        if (typeof window !== 'undefined') {
            window.open(waUrl, '_blank');
        }
    }

    directWhatsApp(category: TicketingCategory): void {
        const msg = `Halo Visidea Studio, saya tertarik dengan layanan *${category.name}*. Bisa dibantu informasi teknis dan penawaran resminya? Terima kasih!`;
        const waUrl = `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(msg)}`;
        if (typeof window !== 'undefined') {
            window.open(waUrl, '_blank');
        }
    }
}
